import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { 
  Calendar, 
  MapPin, 
  Users, 
  Coins, 
  ArrowLeft, 
  MessageCircle,
  Clock,
  CheckCircle,
  X,
  Ticket,
  ExternalLink,
  Info
} from "lucide-react";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { useCircleBooking } from "@/hooks/useCircleBooking";
import { useMyCircleBookings } from "@/hooks/useMyCircleBookings";
import { useCircleConfig } from "@/hooks/useCircleConfig";
import { useCircleApplication } from "@/hooks/useCircleApplication";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { CircleCancelDialog } from "@/components/circle/CircleCancelDialog";
import { CircleAttendeeManager } from "@/components/circle/CircleAttendeeManager";
import { CircleEvent } from "@/types/circle";

export default function CircleEventDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const { bookEvent, cancelBooking } = useCircleBooking();
  const { upcomingBookings } = useMyCircleBookings();
  const { config: circleConfig } = useCircleConfig();
  const { isApproved: isCircleMember, hasApplied, isPending: applicationPending } = useCircleApplication();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // Dedicated credits query - matches pattern from EventBooking.tsx
  const { data: userMembership } = useQuery({
    queryKey: ['user-membership'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_memberships')
        .select('credits_balance')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const { data: event, isLoading } = useQuery({
    queryKey: ["circle-event", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_events")
        .select(`
          *,
          host:profiles!circle_events_host_id_fkey(
            id,
            first_name,
            last_name,
            email,
            avatar_url,
            bio
          )
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as CircleEvent & { host: { bio?: string } };
    },
    enabled: !!id,
  });

  const { data: bookingsData } = useQuery({
    queryKey: ["circle-event-bookings", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_bookings")
        .select(`
          *,
          user:profiles!circle_bookings_user_id_fkey(
            id,
            first_name,
            last_name,
            avatar_url,
            email,
            phone
          )
        `)
        .eq("circle_event_id", id)
        .eq("status", "confirmed");

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner />
        </div>
        <Navigation />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="p-6">
          <Button variant="ghost" onClick={() => navigate("/circle")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold">Event not found</h2>
          </div>
        </div>
        <Navigation />
      </div>
    );
  }

  // Handle lead data - Supabase may return it as an array from joins
  const leadData = Array.isArray(event.host) ? event.host[0] : event.host;
  const leadName = leadData
    ? `${leadData.first_name || ""} ${leadData.last_name || ""}`.trim() || "Community Lead"
    : "Community Lead";
  const leadInitials = leadName.split(" ").map(n => n[0]).join("").toUpperCase();

  const bookedCount = bookingsData?.reduce((acc, b) => acc + b.quantity, 0) || 0;
  const spotsRemaining = event.capacity - bookedCount;
  const isFull = spotsRemaining <= 0;
  const isLead = userId === event.host_id;
  const now = new Date();
  const eventStart = new Date(event.start_time);
  const attendanceWindowMs = (circleConfig.attendance_window_hours || 1) * 60 * 60 * 1000;
  const attendanceStartTime = new Date(eventStart.getTime() - attendanceWindowMs);
  const hasStarted = eventStart <= now;
  const hasEnded = new Date(event.end_time) <= now;
  const canMarkAttendance = now >= attendanceStartTime;

  const myBooking = upcomingBookings.find(b => b.circle_event_id === event.id);
  const isBooked = !!myBooking;

  const creditsBalance = userMembership?.credits_balance || 0;
  const canAfford = creditsBalance >= event.credit_cost;

  const handleBook = () => {
    bookEvent.mutate({ eventId: event.id });
  };

  const handleCancelConfirm = () => {
    if (myBooking) {
      cancelBooking.mutate({ bookingId: myBooking.id });
      setCancelDialogOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate("/circle")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="text-muted-foreground text-sm">Back to Circle</span>
        </div>

        {/* Event Image */}
        {event.image_url ? (
          <img
            src={event.image_url}
            alt={event.title}
            className="w-full h-48 object-cover rounded-xl"
          />
        ) : (
          <div className="w-full h-48 bg-gradient-to-br from-violet-100 to-violet-200 rounded-xl flex items-center justify-center">
            <Users className="w-16 h-16 text-violet-400" />
          </div>
        )}

        {/* Title & Status */}
        <div>
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-2xl font-bold text-foreground">{event.title}</h1>
            {hasEnded && (
              <Badge variant="secondary">Ended</Badge>
            )}
            {!hasEnded && hasStarted && (
              <Badge className="bg-emerald-500">In Progress</Badge>
            )}
          </div>
          
          {event.description && (
            <p className="text-muted-foreground mt-2">{event.description}</p>
          )}
        </div>

        {/* Date, Time, Location */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="font-medium">{format(new Date(event.start_time), "EEEE, MMMM d, yyyy")}</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(event.start_time), "h:mm a")} - {format(new Date(event.end_time), "h:mm a")}
                </p>
              </div>
            </div>

            <Separator />

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="font-medium">{event.venue}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Community Lead Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Community Lead</CardTitle>
          </CardHeader>
          <CardContent className="flex items-start gap-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={leadData?.avatar_url || undefined} />
              <AvatarFallback className="bg-violet-100 text-violet-600">
                {leadInitials}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">{leadName}</p>
              {leadData?.bio && (
                <p className="text-sm text-muted-foreground line-clamp-2">{leadData.bio}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* External Ticket Booking Link - only shown pre-booking */}
        {event.ticket_booking_url && !isBooked && !isLead && !hasEnded && (
          <Card className="border-amber-200 bg-amber-50/40">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                  <Ticket className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Show Ticket Required</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    This event requires a separate ticket purchase. You'll be redirected to an external site outside Nimi.
                  </p>
                </div>
              </div>
              <Button asChild className="w-full bg-amber-500 hover:bg-amber-600">
                <a href={event.ticket_booking_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Book Ticket on External Site
                </a>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Unified Attendees Section */}
        {isLead ? (
          /* Lead view: unified management UI with attendance after event starts */
          <CircleAttendeeManager 
            eventId={event.id} 
            attendees={bookingsData || []}
            capacity={event.capacity}
            eventStarted={canMarkAttendance}
          />
        ) : bookingsData && bookingsData.length > 0 ? (
          /* Non-lead view: simple attendee avatars */
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Attendees</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {bookedCount}/{event.capacity}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {bookingsData.map((booking) => {
                  const name = `${booking.user?.first_name || ""} ${booking.user?.last_name || ""}`.trim();
                  const initials = name.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
                  return (
                    <Avatar key={booking.id} className="w-8 h-8">
                      <AvatarImage src={booking.user?.avatar_url || undefined} />
                      <AvatarFallback className="text-xs bg-violet-100 text-violet-600">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Booking Section */}
        {!hasEnded && (
          <Card className="border-violet-200">
            <CardContent className="p-4">
              {!isCircleMember ? (
                // Non-member view
                <div className="space-y-3 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-violet-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-violet-500" />
                  </div>
                  <div>
                    <h3 className="font-medium">Circle Members Only</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {applicationPending 
                        ? "Your application is under review" 
                        : "Join Circle to book community-led events"}
                    </p>
                  </div>
                  {!hasApplied && (
                    <Button 
                      onClick={() => navigate("/circle")} 
                      className="w-full bg-violet-500 hover:bg-violet-600"
                    >
                      Apply to Join Circle
                    </Button>
                  )}
                </div>
              ) : isBooked ? (
                <div className="space-y-4">
                  <div className="text-center space-y-2">
                    <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="font-semibold text-lg">You're going!</h3>
                  </div>

                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 flex gap-3">
                    <Info className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-violet-900">
                      This is a <strong>community-led event</strong>, not hosted by the Nimi team. It's led by <strong>{leadName}</strong>. Please make sure to book tickets to the show directly (if required) and join the WhatsApp group chat to coordinate with {leadName} and other members.
                    </p>
                  </div>

                  {event.ticket_booking_url && (
                    <Button asChild className="w-full bg-amber-500 hover:bg-amber-600">
                      <a href={event.ticket_booking_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Book Your Ticket (External)
                      </a>
                    </Button>
                  )}

                  {event.whatsapp_group ? (
                    <Button className="w-full bg-emerald-500 hover:bg-emerald-600" asChild>
                      <a href={event.whatsapp_group} target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Join WhatsApp Group
                      </a>
                    </Button>
                  ) : (
                    <p className="text-xs text-center text-muted-foreground">
                      The Community Lead hasn't shared a WhatsApp group yet.
                    </p>
                  )}

                  <Button
                    variant="outline"
                    className="w-full text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel Booking
                  </Button>
                </div>
              ) : hasStarted ? (
                <div className="text-center text-muted-foreground">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                  <p>This event has already started</p>
                </div>
              ) : isFull && !isLead ? (
                <div className="text-center text-muted-foreground">
                  <Users className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                  <p>This event is full</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Coins className="w-5 h-5 text-violet-500" />
                      <span className="font-medium">{event.credit_cost} {event.credit_cost === 1 ? 'credit' : 'credits'}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {spotsRemaining} spot{spotsRemaining !== 1 ? "s" : ""} left
                    </div>
                  </div>

                  {/* Booking Info */}
                  <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Booking Info</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-violet-500 mt-0.5">•</span>
                        <span>This event costs {event.credit_cost} {event.credit_cost === 1 ? 'credit' : 'credits'} to book</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">•</span>
                        <span>Late cancellations require contacting the Community Lead</span>
                      </li>
                    </ul>
                  </div>
                  
                  {isLead ? (
                    <p className="text-sm text-muted-foreground text-center">
                      You're leading this event
                    </p>
                  ) : !canAfford && (
                    <p className="text-sm text-destructive">
                      You have {creditsBalance} credit(s). You need {event.credit_cost} to book.
                    </p>
                  )}
                  
                  <Button
                    onClick={handleBook}
                    disabled={isLead || !canAfford || bookEvent.isPending}
                    className="w-full bg-violet-500 hover:bg-violet-600"
                  >
                    {bookEvent.isPending ? (
                      <>
                        <LoadingSpinner className="w-4 h-4 mr-2" />
                        Booking...
                      </>
                    ) : isLead ? (
                      "You're the Lead"
                    ) : (
                      "Book Now"
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      <Navigation />

      <CircleCancelDialog
        booking={cancelDialogOpen ? myBooking || null : null}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelConfirm}
        isPending={cancelBooking.isPending}
      />
    </div>
  );
}
